import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Key, Plus, Trash2, Copy, Eye, EyeOff, Server, Code, 
  CheckCircle, Clock, AlertTriangle, RefreshCw, ExternalLink, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdServerPage() {
  const [apiKeys, setApiKeys] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editRateLimit, setEditRateLimit] = useState(1000);
  
  const [formData, setFormData] = useState({
    name: '',
    screen_ids: [],
    rate_limit: 1000,
    expires_at: ''
  });

  const [testData, setTestData] = useState({
    api_key: '',
    device_id: 'test-device-001',
    screen_name: 'Test Screen',
    latitude: 40.7128,
    longitude: -74.0060,
    vast_version: '4.0'
  });

  const fetchApiKeys = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApiKeys(data);
      }
    } catch (error) {
      toast.error('Failed to fetch API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchScreens = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScreens(data);
      }
    } catch (error) {
      console.error('Failed to fetch screens');
    }
  }, []);

  useEffect(() => {
    fetchApiKeys();
    fetchScreens();
  }, [fetchApiKeys, fetchScreens]);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        expires_at: formData.expires_at || null
      };
      
      const res = await fetch(`${API_URL}/api/player/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewApiKey(data.api_key);
        setShowDialog(false);
        setShowKeyDialog(true);
        resetForm();
        fetchApiKeys();
        toast.success('API key created successfully');
      } else {
        const text = await res.text();
        let errorMsg = 'Failed to create API key';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          if (text) errorMsg = text;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Failed to create API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to permanently delete this API key? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('API key deleted permanently');
        fetchApiKeys();
      } else {
        toast.error('Failed to delete API key');
      }
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  const handleEditKey = (key) => {
    setEditingKey(key);
    setEditRateLimit(key.rate_limit || 1000);
    setShowEditDialog(true);
  };

  const handleUpdateRateLimit = async (e) => {
    e.preventDefault();
    if (!editingKey) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player/api-keys/${editingKey.id}?rate_limit=${editRateLimit}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Rate limit updated successfully');
        setShowEditDialog(false);
        setEditingKey(null);
        fetchApiKeys();
      } else {
        const text = await res.text();
        let errorMsg = 'Failed to update rate limit';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          if (text) errorMsg = text;
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Failed to update rate limit');
    }
  };

  const handleTestEndpoint = async () => {
    if (!testData.api_key) {
      toast.error('Please enter an API key');
      return;
    }
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/player/ad-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testData)
      });
      
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        const responseText = await res.text();
        
        // Determine if response is VAST XML or Image URL
        const isVast = contentType.includes('xml') || responseText.trim().startsWith('<?xml');
        setTestResult({ 
          success: true, 
          data: {
            format: isVast ? 'vast' : 'image',
            content: responseText
          }
        });
      } else if (res.status === 404) {
        const err = await res.json();
        setTestResult({ success: false, error: err.detail || 'No active campaigns available' });
      } else {
        const text = await res.text();
        let errorMsg = 'Request failed';
        try {
          const err = JSON.parse(text);
          errorMsg = err.detail || errorMsg;
        } catch {
          errorMsg = text || errorMsg;
        }
        setTestResult({ success: false, error: errorMsg });
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const toggleScreen = (screenId) => {
    if (formData.screen_ids.includes(screenId)) {
      setFormData({
        ...formData,
        screen_ids: formData.screen_ids.filter(id => id !== screenId)
      });
    } else {
      setFormData({
        ...formData,
        screen_ids: [...formData.screen_ids, screenId]
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      screen_ids: [],
      rate_limit: 1000,
      expires_at: ''
    });
  };

  const endpointUrl = `${API_URL}/api/player/ad-request`;
  const trackingUrl = `${API_URL}/api/player/track`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Ad Server</h1>
          <p className="text-zinc-400 mt-1">Manage API keys and test your ad-serving endpoint</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Create API Key
        </Button>
      </div>

      <Tabs defaultValue="keys" className="space-y-6">
        <TabsList className="bg-zinc-800">
          <TabsTrigger value="keys" className="data-[state=active]:bg-blue-600">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="docs" className="data-[state=active]:bg-blue-600">
            <Code className="w-4 h-4 mr-2" />
            Documentation
          </TabsTrigger>
          <TabsTrigger value="test" className="data-[state=active]:bg-blue-600">
            <Server className="w-4 h-4 mr-2" />
            Test Endpoint
          </TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="keys">
          <Card className="glass-card border-zinc-800">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Name</TableHead>
                  <TableHead className="text-zinc-500">Key Prefix</TableHead>
                  <TableHead className="text-zinc-500">Screens</TableHead>
                  <TableHead className="text-zinc-500">Rate Limit</TableHead>
                  <TableHead className="text-zinc-500">Total Requests</TableHead>
                  <TableHead className="text-zinc-500">Last Used</TableHead>
                  <TableHead className="text-zinc-500">Status</TableHead>
                  <TableHead className="text-zinc-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                      Loading API keys...
                    </TableCell>
                  </TableRow>
                ) : apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                      No API keys yet. Create one to get started!
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key) => (
                    <TableRow key={key.id} className="border-zinc-800" data-testid={`api-key-row-${key.id}`}>
                      <TableCell className="font-medium text-white">{key.name}</TableCell>
                      <TableCell>
                        <code className="text-cyan-400 bg-zinc-800 px-2 py-1 rounded text-sm">
                          {key.key_prefix}...
                        </code>
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {key.screen_ids?.length > 0 ? `${key.screen_ids.length} screens` : 'All screens'}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleEditKey(key)}
                          className="text-zinc-400 hover:text-blue-400 flex items-center gap-1 group"
                          data-testid={`edit-rate-limit-${key.id}`}
                        >
                          <span>{key.rate_limit}/hr</span>
                          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </TableCell>
                      <TableCell className="text-zinc-300">{key.total_requests?.toLocaleString() || 0}</TableCell>
                      <TableCell className="text-zinc-400">
                        {key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Badge className={key.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}>
                          {key.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditKey(key)}
                            className="text-zinc-400 hover:text-blue-400 hover:bg-blue-500/20"
                            data-testid={`edit-key-btn-${key.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteKey(key.id)}
                            className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="docs" className="space-y-6">
          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Ad Request Endpoint</CardTitle>
              <CardDescription>Request ads from your player/screen device</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500">POST</Badge>
                <code className="text-cyan-400 bg-zinc-800 px-3 py-1 rounded flex-1">{endpointUrl}</code>
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(endpointUrl)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div>
                <h4 className="text-zinc-300 font-medium mb-2">Request Body</h4>
                <pre className="bg-zinc-950 p-4 rounded-lg text-sm overflow-x-auto">
                  <code className="text-zinc-300">{`{
  "api_key": "lmn_your_api_key_here",
  "device_id": "unique-device-identifier",
  "screen_name": "Times Square Billboard",
  "latitude": 40.7580,
  "longitude": -73.9855,
  "vast_version": "4.0"  // Optional: "2.0", "3.0", "4.0", "4.1", "4.2"
}`}</code>
                </pre>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <h4 className="text-blue-400 font-medium mb-2">VAST Version Support</h4>
                <ul className="text-zinc-300 text-sm space-y-1">
                  <li><code className="text-cyan-400">2.0</code> - Basic VAST with tracking events</li>
                  <li><code className="text-cyan-400">3.0</code> - Adds Error element, mute/pause/resume tracking</li>
                  <li><code className="text-cyan-400">4.0</code> - Adds AdVerifications, UniversalAdId (default)</li>
                  <li><code className="text-cyan-400">4.1</code> - Adds Category, ViewableImpression</li>
                  <li><code className="text-cyan-400">4.2</code> - Adds progress tracking at 5s, 15s, 30s</li>
                </ul>
              </div>

              <div>
                <h4 className="text-zinc-300 font-medium mb-2">Response (Image Creative)</h4>
                <div className="bg-zinc-950 p-4 rounded-lg text-sm overflow-x-auto">
                  <p className="text-zinc-500 mb-2">Content-Type: text/plain</p>
                  <code className="text-cyan-400">https://your-domain.com/api/uploads/creatives/image.jpg</code>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Returns the image URL directly as plain text</p>
              </div>

              <div>
                <h4 className="text-zinc-300 font-medium mb-2">Response (Video Creative)</h4>
                <div className="bg-zinc-950 p-4 rounded-lg text-sm overflow-x-auto">
                  <p className="text-zinc-500 mb-2">Content-Type: application/xml</p>
                  <code className="text-purple-400">{`<?xml version="1.0" encoding="UTF-8"?>
<VAST version="4.0">
  <Ad id="creative-uuid">
    <InLine>
      <AdSystem>Lumina OS Ad Server</AdSystem>
      <Impression>...</Impression>
      <Creatives>...</Creatives>
    </InLine>
  </Ad>
</VAST>`}</code>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Returns VAST 4.0 XML directly with embedded tracking pixels</p>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white">Tracking Endpoint</CardTitle>
              <CardDescription>Report impressions, clicks, and video quartiles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-500">POST</Badge>
                <code className="text-cyan-400 bg-zinc-800 px-3 py-1 rounded flex-1">{trackingUrl}</code>
                <Button variant="ghost" size="icon" onClick={() => copyToClipboard(trackingUrl)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              <div>
                <h4 className="text-zinc-300 font-medium mb-2">Request Body</h4>
                <pre className="bg-zinc-950 p-4 rounded-lg text-sm overflow-x-auto">
                  <code className="text-zinc-300">{`{
  "api_key": "lmn_your_api_key_here",
  "creative_id": "uuid-from-ad-response",
  "campaign_id": "uuid-from-ad-response",
  "device_id": "unique-device-identifier",
  "screen_name": "Times Square Billboard",
  "latitude": 40.7580,
  "longitude": -73.9855,
  "event_type": "impression"
  // event_type options:
  // "impression" - Ad was displayed
  // "click" - User clicked the ad
  // "firstQuartile" - Video reached 25%
  // "midpoint" - Video reached 50%
  // "thirdQuartile" - Video reached 75%
  // "complete" - Video finished
}`}</code>
                </pre>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Important Notes
                </h4>
                <ul className="text-zinc-300 text-sm space-y-1 list-disc list-inside">
                  <li>Always call the tracking endpoint after displaying an ad</li>
                  <li>For videos, report quartile events as the video plays</li>
                  <li>Include geo data for location-based analytics</li>
                  <li>VAST XML includes tracking pixels - they auto-fire if using a VAST player</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Endpoint Tab */}
        <TabsContent value="test">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Test Ad Request</CardTitle>
                <CardDescription>Send a test request to verify your setup</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">API Key</Label>
                  <Input
                    value={testData.api_key}
                    onChange={(e) => setTestData({ ...testData, api_key: e.target.value })}
                    placeholder="lmn_your_api_key_here"
                    className="bg-zinc-800 border-zinc-700 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Device ID</Label>
                  <Input
                    value={testData.device_id}
                    onChange={(e) => setTestData({ ...testData, device_id: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Screen Name</Label>
                  <Input
                    value={testData.screen_name}
                    onChange={(e) => setTestData({ ...testData, screen_name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testData.latitude}
                      onChange={(e) => setTestData({ ...testData, latitude: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={testData.longitude}
                      onChange={(e) => setTestData({ ...testData, longitude: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">VAST Version (for video)</Label>
                  <Select value={testData.vast_version} onValueChange={(v) => setTestData({ ...testData, vast_version: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      <SelectItem value="2.0">VAST 2.0</SelectItem>
                      <SelectItem value="3.0">VAST 3.0</SelectItem>
                      <SelectItem value="4.0">VAST 4.0 (Default)</SelectItem>
                      <SelectItem value="4.1">VAST 4.1</SelectItem>
                      <SelectItem value="4.2">VAST 4.2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleTestEndpoint} 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={testing}
                >
                  {testing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Server className="w-4 h-4 mr-2" />
                      Send Test Request
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className="glass-card border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Response</CardTitle>
              </CardHeader>
              <CardContent>
                {testResult === null ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Server className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Send a test request to see the response</p>
                  </div>
                ) : testResult.success ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Success!</span>
                      </div>
                      <Badge className={testResult.data?.format === 'vast' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                        {testResult.data?.format === 'vast' ? 'VAST XML' : 'Image URL'}
                      </Badge>
                    </div>
                    <pre className="bg-zinc-950 p-4 rounded-lg text-sm overflow-auto max-h-80">
                      <code className="text-zinc-300">
                        {testResult.data?.content}
                      </code>
                    </pre>
                    {testResult.data?.format === 'image' && testResult.data?.content && (
                      <div className="mt-4">
                        <p className="text-zinc-400 text-sm mb-2">Preview:</p>
                        <img 
                          src={testResult.data.content.startsWith('http') ? testResult.data.content : `${API_URL}${testResult.data.content}`}
                          alt="Ad Preview"
                          className="max-h-48 rounded border border-zinc-700"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-rose-400">
                      <AlertTriangle className="w-5 h-5" />
                      <span className="font-medium">Error</span>
                    </div>
                    <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-4">
                      <p className="text-rose-300">{testResult.error}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create API Key Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create API Key</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateKey} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Key Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Production Player Key"
                className="bg-zinc-800 border-zinc-700"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Rate Limit (requests/hour)</Label>
              <Input
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Expires (optional)</Label>
              <Input
                type="datetime-local"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Restrict to Screens (optional)</Label>
              <div className="max-h-32 overflow-y-auto border border-zinc-800 rounded-lg">
                {screens.map((screen) => (
                  <div key={screen.id} className="flex items-center gap-3 p-2 border-b border-zinc-800 last:border-0">
                    <Checkbox
                      checked={formData.screen_ids.includes(screen.id)}
                      onCheckedChange={() => toggleScreen(screen.id)}
                    />
                    <span className="text-white text-sm">{screen.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-zinc-500 text-xs">Leave empty to allow access to all screens.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Key</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              API Key Created
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <p className="text-amber-300 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Save this key now! It will not be shown again.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Your API Key</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={newApiKey}
                  readOnly
                  className="bg-zinc-800 border-zinc-700 font-mono text-cyan-400"
                />
                <Button variant="outline" onClick={() => copyToClipboard(newApiKey)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowKeyDialog(false)} className="bg-blue-600 hover:bg-blue-700">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rate Limit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Edit Rate Limit
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpdateRateLimit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">API Key</Label>
              <div className="flex items-center gap-2">
                <code className="text-cyan-400 bg-zinc-800 px-3 py-2 rounded text-sm flex-1">
                  {editingKey?.name} ({editingKey?.key_prefix}...)
                </code>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Rate Limit (requests/hour)</Label>
              <Input
                type="number"
                min={1}
                max={100000}
                value={editRateLimit}
                onChange={(e) => setEditRateLimit(parseInt(e.target.value) || 1000)}
                className="bg-zinc-800 border-zinc-700"
                data-testid="edit-rate-limit-input"
              />
              <p className="text-zinc-500 text-xs">Set the maximum number of ad requests allowed per hour (1 - 100,000)</p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <p className="text-blue-300 text-sm">
                Current: <strong>{editingKey?.rate_limit}/hr</strong> → New: <strong>{editRateLimit}/hr</strong>
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-rate-limit-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
